import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import config from '../config.js';
import { detailToString } from '../utils/apiError';
import { adminPinFromSession } from '../utils/securityApi';

// Map route paths to page context names
const PAGE_CONTEXT_MAP = {
  '/': 'home',
  '/dashboard': 'dashboard',
  '/queue': 'job-queue',
  '/jobs': 'jobs-list',
  '/job-entry': 'job-entry',
  '/scheduling': 'scheduling',
  '/shipping-queue': 'shipping-queue',
  '/parts': 'parts-management',
  '/purchase-orders': 'purchase-orders',
  '/materials': 'materials-management',
  '/assemblies': 'assemblies-management',
  '/consumables': 'consumables-management',
  '/customers': 'customer-management',
  '/vendors': 'vendor-management',
  '/receiving': 'receiving',
  '/invoices': 'invoices',
  '/quotes': 'quotes',
  '/sales-orders': 'sales-orders',
  '/reportbuilder': 'report-builder',
  '/cameras': 'cameras',
  '/gauges': 'gauge-management',
  '/pfmea-setup': 'pfmea-setup',
  '/shop-health': 'shop-health',
  '/admin-data-transfer': 'admin-data-transfer',
  '/admin-dev-tools': 'admin-dev-tools',
  '/users': 'user-management',
  '/customer-po-queue': 'customer-po-queue',
  '/progress': 'progress',
  '/cut-sheets': 'cut-sheets',
  '/cut-lists': 'cut-lists',
};

// Extract route params (e.g., /jobs/123 -> { jobId: "123" })
function extractRouteParams(pathname) {
  const jobMatch = pathname.match(/^\/jobs\/(\d+)/);
  if (jobMatch) return { jobId: jobMatch[1] };
  return {};
}

// Get the page context name, handling parameterized routes
function getPageName(pathname) {
  // Check for exact match first
  if (PAGE_CONTEXT_MAP[pathname]) return PAGE_CONTEXT_MAP[pathname];
  // Check for parameterized routes
  if (/^\/jobs\/\d+/.test(pathname)) return 'job-details';
  // Fallback to the pathname itself
  return pathname;
}

export default function AiAssistantDrawer() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [approvingFixId, setApprovingFixId] = useState(null);
  const [expandedQueries, setExpandedQueries] = useState({});
  const [selectedModel, setSelectedModel] = useState('auto');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();

  // Admin-only: check if user is admin
  const isAdmin = () => {
    const role = sessionStorage.getItem("userRole") || "";
    return role.toLowerCase() === "admin";
  };

  // Clear conversation on page navigation
  useEffect(() => {
    setMessages([]);
    setConversationId(null);
    setExpandedQueries({});
  }, [location.pathname]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current.focus(), 100);
    }
  }, [isOpen]);

  const getPageContext = useCallback(() => {
    const ctx = {
      page: getPageName(location.pathname),
      ...extractRouteParams(location.pathname),
    };
    // Merge in selected item context from the active page
    if (window.__aiContext) {
      Object.assign(ctx, window.__aiContext);
    }
    return ctx;
  }, [location.pathname]);

  const sendMessage = useCallback(async () => {
    if (!input.trim() || isLoading) return;
    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const res = await fetch(`${config.AI_BASE_URL}/ai/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': adminPinFromSession() || '' },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          page_context: getPageContext(),
          model: selectedModel,
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(detailToString(errData.detail) || `API error: ${res.status}`);
      }

      const data = await res.json();
      setConversationId(data.conversation_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        queriesRun: data.queries_run || [],
        proposedFix: data.proposed_fix || null,
        modelUsed: data.model_used || null,
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err.message}`,
        queriesRun: [],
        proposedFix: null,
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, conversationId, getPageContext, selectedModel]);

  const handleApprove = useCallback(async (fixId, approved) => {
    setApprovingFixId(fixId);
    try {
      const res = await fetch(`${config.AI_BASE_URL}/ai/approve-fix`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': adminPinFromSession() || '' },
        body: JSON.stringify({
          fix_id: fixId,
          conversation_id: conversationId,
          approved,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(detailToString(data.detail) || 'Failed');
      }

      setMessages(prev => prev.map(msg => {
        if (msg.proposedFix && msg.proposedFix.fix_id === fixId) {
          return {
            ...msg,
            proposedFix: {
              ...msg.proposedFix,
              status: approved ? 'executed' : 'rejected',
              rows_affected: data.rows_affected,
            },
          };
        }
        return msg;
      }));
    } catch (err) {
      alert(`Failed to ${approved ? 'approve' : 'reject'} fix: ${err.message}`);
    } finally {
      setApprovingFixId(null);
    }
  }, [conversationId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleQueries = (msgIndex) => {
    setExpandedQueries(prev => ({ ...prev, [msgIndex]: !prev[msgIndex] }));
  };

  const clearConversation = () => {
    setMessages([]);
    setConversationId(null);
    setExpandedQueries({});
  };

  // --- Render helpers ---

  const renderFixCard = (fix) => {
    if (!fix) return null;
    const isPending = fix.status === 'pending';
    const isExecuted = fix.status === 'executed';
    const isRejected = fix.status === 'rejected';

    return (
      <div style={{
        ...styles.fixCard,
        ...(isExecuted ? styles.fixCardExecuted : {}),
        ...(isRejected ? styles.fixCardRejected : {}),
      }}>
        <div style={styles.fixHeader}>
          {isPending && 'Proposed Fix'}
          {isExecuted && 'Fix Applied'}
          {isRejected && 'Fix Rejected'}
        </div>
        <p style={styles.fixDescription}>{fix.description}</p>
        <div style={styles.fixSql}>{fix.sql}</div>
        {fix.affected_rows_preview && fix.affected_rows_preview.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <div style={styles.fixPreviewLabel}>
              Affected rows ({fix.affected_rows_preview.length}):
            </div>
            <div style={styles.fixPreviewTable}>
              {fix.affected_rows_preview.slice(0, 10).map((row, i) => (
                <div key={i} style={styles.fixPreviewRow}>
                  {Object.entries(row).map(([k, v]) => (
                    <span key={k} style={styles.fixPreviewCell}>
                      <strong>{k}:</strong> {v === null ? 'NULL' : String(v)}
                    </span>
                  ))}
                </div>
              ))}
              {fix.affected_rows_preview.length > 10 && (
                <div style={styles.fixPreviewMore}>
                  ...and {fix.affected_rows_preview.length - 10} more rows
                </div>
              )}
            </div>
          </div>
        )}
        {isPending && (
          <div style={styles.fixButtons}>
            <button
              style={styles.approveBtn}
              onClick={() => handleApprove(fix.fix_id, true)}
              disabled={approvingFixId === fix.fix_id}
            >
              {approvingFixId === fix.fix_id ? 'Applying...' : 'Approve & Execute'}
            </button>
            <button
              style={styles.rejectBtn}
              onClick={() => handleApprove(fix.fix_id, false)}
              disabled={approvingFixId === fix.fix_id}
            >
              Reject
            </button>
          </div>
        )}
        {isExecuted && fix.rows_affected !== undefined && (
          <div style={styles.fixResult}>
            {fix.rows_affected} row{fix.rows_affected !== 1 ? 's' : ''} updated
          </div>
        )}
      </div>
    );
  };

  const renderQueriesRun = (queries, msgIndex) => {
    if (!queries || queries.length === 0) return null;
    const isExpanded = expandedQueries[msgIndex];

    return (
      <div style={{ marginTop: '4px' }}>
        <span
          style={styles.queriesToggle}
          onClick={() => toggleQueries(msgIndex)}
        >
          {isExpanded ? '\u25BC' : '\u25B6'} {queries.length} quer{queries.length === 1 ? 'y' : 'ies'} run
        </span>
        {isExpanded && (
          <div style={styles.queriesList}>
            {queries.map((q, i) => (
              <div key={i} style={styles.queryItem}>
                <div style={styles.queryDb}>{q.database || q.tool}</div>
                <div style={styles.querySql}>{q.sql}</div>
                <div style={styles.queryResult}>
                  {q.error ? (
                    <span style={{ color: '#dc3545' }}>Error: {q.error}</span>
                  ) : (
                    <span>{q.row_count} row{q.row_count !== 1 ? 's' : ''}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderMessage = (msg, index) => {
    if (msg.role === 'user') {
      return (
        <div key={index} style={styles.userMessageContainer}>
          <div style={styles.userMessage}>{msg.content}</div>
        </div>
      );
    }

    const modelShort = msg.modelUsed
      ? msg.modelUsed.includes('opus') ? 'Opus'
        : msg.modelUsed.includes('sonnet') ? 'Sonnet'
        : msg.modelUsed.includes('haiku') ? 'Haiku' : ''
      : '';

    return (
      <div key={index} style={styles.assistantMessageContainer}>
        <div style={{
          ...styles.assistantMessage,
          ...(msg.isError ? styles.errorMessage : {}),
        }}>
          {msg.content}
        </div>
        <div style={styles.messageMeta}>
          {modelShort && <span style={styles.modelBadge}>{modelShort}</span>}
          {renderQueriesRun(msg.queriesRun, index)}
        </div>
        {renderFixCard(msg.proposedFix)}
      </div>
    );
  };

  // Don't render anything if not admin
  if (!isAdmin()) return null;

  return (
    <>
      {/* Floating button */}
      {!isOpen && (
        <button
          style={styles.floatingButton}
          onClick={() => setIsOpen(true)}
          title="Ask AI Assistant"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {/* Drawer */}
      {isOpen && (
        <div style={styles.drawerOverlay}>
          {/* Header */}
          <div style={styles.drawerHeader}>
            <div style={styles.headerLeft}>
              <span style={styles.headerTitle}>AI Assistant</span>
              <span style={styles.headerPage}>
                {getPageName(location.pathname)}
              </span>
            </div>
            <div style={styles.headerRight}>
              {/* Model toggle */}
              <div style={styles.modelToggle}>
                {[
                  { key: 'auto', label: 'Auto' },
                  { key: 'sonnet', label: 'Sonnet' },
                  { key: 'opus', label: 'Opus' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    style={{
                      ...styles.modelBtn,
                      ...(selectedModel === opt.key ? styles.modelBtnActive : {}),
                    }}
                    onClick={() => setSelectedModel(opt.key)}
                    title={
                      opt.key === 'auto' ? 'Auto-select: Sonnet for reads, Opus for writes/complex' :
                      opt.key === 'sonnet' ? 'Fast & capable - great for queries & lookups' :
                      'Most powerful - best for complex diagnostics & data fixes'
                    }
                  >
                    <span style={styles.modelLabel}>{opt.label}</span>
                  </button>
                ))}
              </div>
              {messages.length > 0 && (
                <button
                  style={styles.clearBtn}
                  onClick={clearConversation}
                  title="Clear conversation"
                >
                  Clear
                </button>
              )}
              <button
                style={styles.closeBtn}
                onClick={() => setIsOpen(false)}
                title="Close"
              >
                &times;
              </button>
            </div>
          </div>

          {/* Messages */}
          <div style={styles.messagesArea}>
            {messages.length === 0 && !isLoading && (
              <div style={styles.welcomeMessage}>
                <div style={styles.welcomeIcon}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="1.5">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </div>
                <p style={styles.welcomeTitle}>JobQ AI Assistant</p>
                <p style={styles.welcomeText}>
                  Ask me about jobs, materials, deliveries, pricing, scheduling, and more.
                  I can query the database and explain how things are calculated.
                </p>
                <div style={styles.welcomeExamples}>
                  <div style={styles.exampleChip} onClick={() => { setInput('When are we expecting the next material delivery from Ryerson?'); }}>
                    Next Ryerson delivery
                  </div>
                  <div style={styles.exampleChip} onClick={() => { setInput('What jobs are late or overdue this week?'); }}>
                    Late jobs this week
                  </div>
                  <div style={styles.exampleChip} onClick={() => { setInput('What vendor has the lowest recent purchase price on 11 ga 60x120 material?'); }}>
                    Lowest price 11ga
                  </div>
                  <div style={styles.exampleChip} onClick={() => { setInput('How is assembly weight calculated in the code?'); }}>
                    How is weight calculated?
                  </div>
                </div>
              </div>
            )}

            {messages.map(renderMessage)}

            {isLoading && (
              <div style={styles.assistantMessageContainer}>
                <div style={styles.thinkingMessage}>
                  <div style={styles.thinkingDots}>
                    <span style={{ ...styles.dot, animationDelay: '0s' }}>*</span>
                    <span style={{ ...styles.dot, animationDelay: '0.3s' }}>*</span>
                    <span style={{ ...styles.dot, animationDelay: '0.6s' }}>*</span>
                  </div>
                  <span style={styles.thinkingText}>Investigating...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div style={styles.inputArea}>
            <textarea
              ref={inputRef}
              style={styles.input}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about jobs, materials, deliveries..."
              rows={1}
              disabled={isLoading}
            />
            <button
              style={{
                ...styles.sendButton,
                ...(isLoading || !input.trim() ? styles.sendButtonDisabled : {}),
              }}
              onClick={sendMessage}
              disabled={isLoading || !input.trim()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Inline animation keyframes */}
      <style>{`
        @keyframes aiBlink {
          0%, 80%, 100% { opacity: 0.3; }
          40% { opacity: 1; }
        }
      `}</style>
    </>
  );
}


// --- Styles ---
const styles = {
  // Floating button
  floatingButton: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },

  // Drawer
  drawerOverlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: '480px',
    maxWidth: '100vw',
    backgroundColor: '#fff',
    boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
    zIndex: 1040,
    display: 'flex',
    flexDirection: 'column',
  },

  // Header
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 16px',
    borderBottom: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  headerTitle: {
    fontWeight: 'bold',
    fontSize: '15px',
    color: '#212529',
  },
  headerPage: {
    fontSize: '11px',
    color: '#6c757d',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  clearBtn: {
    padding: '4px 10px',
    fontSize: '12px',
    color: '#6c757d',
    backgroundColor: 'transparent',
    border: '1px solid #dee2e6',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#6c757d',
    lineHeight: '1',
    padding: '0 4px',
  },

  // Model toggle
  modelToggle: {
    display: 'flex',
    gap: '1px',
    backgroundColor: '#dee2e6',
    borderRadius: '6px',
    padding: '1px',
    overflow: 'hidden',
  },
  modelBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    padding: '3px 8px',
    fontSize: '11px',
    border: 'none',
    backgroundColor: '#fff',
    color: '#6c757d',
    cursor: 'pointer',
    transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  modelBtnActive: {
    backgroundColor: '#007bff',
    color: '#fff',
    fontWeight: '600',
  },
  modelLabel: {
    fontSize: '11px',
  },

  // Messages area
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
  },

  // User messages
  userMessageContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '10px',
  },
  userMessage: {
    backgroundColor: '#007bff',
    color: '#fff',
    padding: '8px 14px',
    borderRadius: '16px 16px 2px 16px',
    maxWidth: '85%',
    wordBreak: 'break-word',
    fontSize: '14px',
    lineHeight: '1.4',
  },

  // Assistant messages
  assistantMessageContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: '10px',
    maxWidth: '92%',
  },
  assistantMessage: {
    backgroundColor: '#f1f3f5',
    color: '#212529',
    padding: '10px 14px',
    borderRadius: '16px 16px 16px 2px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: '13px',
    lineHeight: '1.55',
  },
  errorMessage: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  messageMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
  },
  modelBadge: {
    fontSize: '10px',
    color: '#868e96',
    backgroundColor: '#f1f3f5',
    padding: '1px 6px',
    borderRadius: '3px',
    whiteSpace: 'nowrap',
  },

  // Thinking indicator
  thinkingMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#f1f3f5',
    padding: '10px 14px',
    borderRadius: '16px 16px 16px 2px',
  },
  thinkingDots: {
    display: 'flex',
    gap: '3px',
  },
  dot: {
    fontSize: '14px',
    color: '#007bff',
    animation: 'aiBlink 1.4s infinite both',
  },
  thinkingText: {
    fontSize: '13px',
    color: '#6c757d',
    fontStyle: 'italic',
  },

  // Input area
  inputArea: {
    display: 'flex',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid #dee2e6',
    backgroundColor: '#f8f9fa',
    flexShrink: 0,
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #ced4da',
    borderRadius: '20px',
    fontSize: '14px',
    outline: 'none',
    resize: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.4',
    maxHeight: '100px',
    overflowY: 'auto',
  },
  sendButton: {
    width: '40px',
    height: '40px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'background-color 0.2s',
  },
  sendButtonDisabled: {
    backgroundColor: '#adb5bd',
    cursor: 'not-allowed',
  },

  // Queries run section
  queriesToggle: {
    fontSize: '11px',
    color: '#6c757d',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-block',
    marginTop: '4px',
  },
  queriesList: {
    marginTop: '4px',
    borderRadius: '6px',
    overflow: 'hidden',
    border: '1px solid #e9ecef',
  },
  queryItem: {
    padding: '6px 8px',
    borderBottom: '1px solid #e9ecef',
    fontSize: '11px',
  },
  queryDb: {
    display: 'inline-block',
    backgroundColor: '#e9ecef',
    padding: '1px 6px',
    borderRadius: '3px',
    fontSize: '10px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#495057',
    marginBottom: '3px',
  },
  querySql: {
    fontFamily: 'monospace',
    fontSize: '10px',
    color: '#495057',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    lineHeight: '1.3',
  },
  queryResult: {
    fontSize: '10px',
    color: '#6c757d',
    marginTop: '2px',
  },

  // Fix proposal card
  fixCard: {
    border: '2px solid #ffc107',
    borderRadius: '8px',
    padding: '12px',
    margin: '8px 0',
    backgroundColor: '#fffce6',
  },
  fixCardExecuted: {
    borderColor: '#28a745',
    backgroundColor: '#f0fff0',
  },
  fixCardRejected: {
    borderColor: '#dc3545',
    backgroundColor: '#fff5f5',
    opacity: 0.7,
  },
  fixHeader: {
    fontWeight: 'bold',
    fontSize: '13px',
    marginBottom: '6px',
  },
  fixDescription: {
    fontSize: '12px',
    color: '#495057',
    marginBottom: '6px',
    lineHeight: '1.4',
    margin: '0 0 6px 0',
  },
  fixSql: {
    fontFamily: 'monospace',
    fontSize: '11px',
    backgroundColor: '#f8f9fa',
    padding: '8px',
    borderRadius: '4px',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    border: '1px solid #e9ecef',
    lineHeight: '1.3',
  },
  fixPreviewLabel: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#495057',
    marginBottom: '4px',
  },
  fixPreviewTable: {
    fontSize: '10px',
    fontFamily: 'monospace',
    maxHeight: '150px',
    overflowY: 'auto',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
    padding: '4px',
    border: '1px solid #e9ecef',
  },
  fixPreviewRow: {
    padding: '3px 4px',
    borderBottom: '1px solid #e9ecef',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  fixPreviewCell: {
    whiteSpace: 'nowrap',
  },
  fixPreviewMore: {
    padding: '4px',
    fontStyle: 'italic',
    color: '#6c757d',
  },
  fixButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '10px',
  },
  approveBtn: {
    padding: '6px 16px',
    backgroundColor: '#28a745',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
  },
  rejectBtn: {
    padding: '6px 16px',
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  fixResult: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#28a745',
    fontWeight: 'bold',
  },

  // Welcome message
  welcomeMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    textAlign: 'center',
    flex: 1,
  },
  welcomeIcon: {
    marginBottom: '12px',
    opacity: 0.5,
  },
  welcomeTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#495057',
    margin: '0 0 8px 0',
  },
  welcomeText: {
    fontSize: '13px',
    color: '#6c757d',
    margin: '0 0 20px 0',
    lineHeight: '1.5',
    maxWidth: '300px',
  },
  welcomeExamples: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    justifyContent: 'center',
  },
  exampleChip: {
    padding: '6px 12px',
    backgroundColor: '#e9ecef',
    borderRadius: '16px',
    fontSize: '12px',
    color: '#495057',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};
